import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { Component, effect, ElementRef, inject, Injectable, OnInit, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AttachmentModel } from '@models/module/fields/main';
import { CipoFormModule } from '@shared/modules';
import { ItvDateTimePipe } from '@shared/pipes';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { FileService } from '@shared/services/file.service';
import { AiStore, UserStore } from '@signal-store';
import type { AiReasoningStep } from '@signal-store/models/ai-store.model';
import { AiChatContextSelection } from '@system/search/cipo-ai/cipo-ai.service';
import { MarkdownModule } from 'ngx-markdown';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

export type AgentRole = 'user' | 'assistant' | 'system';

export enum AgentRunThreadMessageItemType {
  Text = 0,
  Reference = 1,
  FollowUp = 2,
}

export enum AgentRunThreadMessageSourceType {
  File = 0,
  Url = 1,
}

export type AgentRunThread = {
  threadId: string;
  createdAt: string;
  messages: AgentRunThreadMessage[];
};

export type AgentRunThreadMessage = {
  role: AgentRole;
  createdAt: string;
  items: AgentRunThreadMessageItem[];
  reasoningSteps?: AiReasoningStep[];
};

export type AgentRunThreadMessageItem = {
  itemType?: AgentRunThreadMessageItemType;
  text?: string;
  sourceType?: AgentRunThreadMessageSourceType;
  sourceReference?: string;
  fileId?: string;
  fileVersionId?: string;
  fileMimeType?: string;
  followUpPrompt?: string;
};

/**
 * Component-scoped OverlayContainer for the AI sidebar.
 * Keeps MatMenu DOM inside the sidebar, so .main-app-content's overflow:hidden
 * does not clip it. Positioning still uses the real viewport because the
 * _getNarrowedViewportRect patch detects that the host is outside .main-app-content
 * and falls back to CDK's default behavior.
 */
@Injectable()
class SidebarOverlayContainer extends OverlayContainer {
  constructor() {
    super(inject(DOCUMENT));
  }

  attach(sidebarEl: HTMLElement): void {
    if (this._containerElement) return;
    const container = this._document.createElement('div');
    container.classList.add('cdk-overlay-container');
    sidebarEl.appendChild(container);
    this._containerElement = container;
  }
}

@Component({
  selector: 'cipo-ai',
  templateUrl: './cipo-ai.component.html',
  styleUrls: ['./cipo-ai.component.scss'],
  imports: [
    CipoFormModule,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    MarkdownModule,
    ItvDateTimePipe,
    SafeHtmlPipe,
    MatDividerModule,
    MatCheckboxModule,
    MatListModule,
    MatTooltipModule,
  ],
  providers: [SidebarOverlayContainer, { provide: OverlayContainer, useExisting: SidebarOverlayContainer }],
})
export class CipoAiContainerComponent implements OnInit {
  protected userStore = inject(UserStore);
  protected aiStore = inject(AiStore);
  private fileService = inject(FileService);
  private host = inject(ElementRef) as ElementRef<HTMLElement>;
  private sidebarContainer = inject(SidebarOverlayContainer);

  private searchInput = viewChild<ElementRef<HTMLTextAreaElement>>('searchInput');
  private responseContainer = viewChild<ElementRef<HTMLDivElement>>('responseContainer');

  searchControl = new FormControl('');
  restrictToCurrentContextControl = new FormControl(true, { nonNullable: true });
  readonly itemTypes = AgentRunThreadMessageItemType;
  readonly sourceTypes = AgentRunThreadMessageSourceType;

  ngOnInit(): void {
    this.sidebarContainer.attach(this.host.nativeElement);
  }

  loadingEffect = effect(() => {
    if (this.aiStore.loading()) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  });

  responseEffect = effect(() => {
    const thread = this.aiStore.thread();
    if (thread) {
      this.scrollToBottom();
      // should focus input after the scroll to bottom
      this.focusInput();
    } else {
      this.focusInput();
    }
  });

  search() {
    const text = this.searchControl.value;
    if (!text) return;

    this.aiStore.askQuestion({ text, contextSelection: this.getContextSelection() });
    this.searchControl.setValue('');
  }

  isFileReference(item: AgentRunThreadMessageItem): boolean {
    const isReference = item.itemType === this.itemTypes.Reference;
    const isFile = item.sourceType === this.sourceTypes.File;
    return isReference && isFile && !!item.fileId && !!item.fileVersionId && !!item.fileMimeType && !!item.text;
  }

  isUrlReference(item: AgentRunThreadMessageItem): boolean {
    const isReference = item.itemType === this.itemTypes.Reference;
    const isUrl = item.sourceType === this.sourceTypes.Url;
    return isReference && isUrl && !!item.sourceReference && !!item.text;
  }

  openExternalReference(item: AgentRunThreadMessageItem, event: MouseEvent): void {
    event.preventDefault();

    if (!this.isUrlReference(item)) {
      return;
    }

    window.open(item.sourceReference, '_blank', 'noopener,noreferrer');
  }

  isFollowUp(item: AgentRunThreadMessageItem): boolean {
    return item.itemType === this.itemTypes.FollowUp && !!item.followUpPrompt && !!item.text;
  }

  runFollowUp(item: AgentRunThreadMessageItem, event: MouseEvent): void {
    event.preventDefault();

    const prompt = item.followUpPrompt?.trim();
    if (!prompt || this.aiStore.loading()) {
      return;
    }

    this.searchControl.setValue(prompt);
    this.aiStore.askQuestion({ text: prompt, contextSelection: this.getContextSelection() });
    this.searchControl.setValue('');
  }

  openReferencedFile(item: AgentRunThreadMessageItem, event: MouseEvent): void {
    event.preventDefault();

    if (!item.fileId || !item.fileVersionId || !item.text || !item.fileMimeType) {
      return;
    }

    const fileId = Number(item.fileId);
    const fileVersionId = Number(item.fileVersionId);
    if (!Number.isInteger(fileId) || !Number.isInteger(fileVersionId)) {
      return;
    }

    const attachment: AttachmentModel = {
      id: fileId,
      name: item.text,
      version: 1,
      versionId: fileVersionId,
      parentId: 0,
      mimeType: item.fileMimeType as AttachmentModel['mimeType'],
    };

    this.fileService.openFile([attachment], 0, true);
  }

  private getContextSelection(): AiChatContextSelection {
    return {
      restrictToCurrentContext: this.restrictToCurrentContextControl.value,
      programEntityInstanceId: this.userStore.program()?.entityInstanceId,
      projectEntityInstanceId: this.userStore.project()?.entityInstanceId,
      contractEntityInstanceId: this.userStore.contract()?.entityInstanceId,
    };
  }

  private scrollToBottom() {
    setTimeout(() => {
      const container = this.responseContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  }

  private focusInput() {
    setTimeout(() => {
      const inputElement = this.searchInput()?.nativeElement;
      if (!inputElement) {
        return;
      }

      inputElement.focus();

      const textLength = inputElement.value.length;

      // Set selection range to the end
      inputElement.setSelectionRange(textLength, textLength);
      // Scroll to show the end of the text
      inputElement.scrollLeft = inputElement.scrollWidth;
    }, 200);
  }
}
